<template>
	<Row :gutter="[16, 8]">
		<Col :span="18" :offset="0" >
			<VerticalLayout>
				<StatsChartTitleWithDescription
					titleKey="stats.caster-dynamics.trafficChart"
					descriptionKey="stats.caster-dynamics.trafficChartTooltip"
					titleClass="pd-lg-x pd-md-top"
				/>
				<TimeSeriesChart :isLoading="isLoading" :series="trafficChartSeries" :options="trafficChartOptions" />

				<StatsChartTitleWithDescription
					titleKey="stats.caster-dynamics.connectionsChart"
					descriptionKey="stats.caster-dynamics.connectionsChartTooltip"
				/>
				<TimeSeriesChart :isLoading="isLoading" :series="connectionsChartSeries" :options="connectionsChartOptions" />

				<StatsChartTitleWithDescription
					titleKey="stats.caster-dynamics.usersChart"
					descriptionKey="stats.caster-dynamics.usersChartTooltip"
				/>
				<TimeSeriesChart :isLoading="isLoading" :series="userTrafficChartSeries" :options="userTrafficChartOptions" />

			</VerticalLayout>
		</Col>
		<Col :span="6" :offset="0" >
			<VerticalLayout>
				<Affix :offsetTop="77" :target="() => wrapper">
					<Card :bordered="false">
						<VerticalLayout>
							<DateRangeChooser v-model="datesRange" @change="casterDynamicsGet" />
							{{ $t('stats.caster-dynamics.usersLabel') }}
							<Select
								class="width-100"
								v-model:value="usersSelected"
								mode="multiple"
								:allowClear="true"
								:options="allUsersOptions"
							/>
						</VerticalLayout>
					</Card>
				</Affix>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { Row, Col, Card, Select, Affix } from 'ant-design-vue';

import Api from '@/api/Api';

import { useSessionStore } from '@/stores/session';
import { useDateRangeAdapter } from '@/utils/useDateRangeAdapter';

import { getBaseOptions, generateSeries, setXAxisRange, nullSafeFormatter } from '@/utils/chartsUtils';

import VerticalLayout from '@/components/common/VerticalLayout';
import DateRangeChooser from '@/components/common/DateRangeChooser';

import TimeSeriesChart from '@/components/stats/TimeSeriesChart';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';

import { useProvideInjectWrapper } from '@/utils/useProvideInject';


const DISPLAY_TITLES = {
	inTrafficDiff: 'Inward',
	outTrafficDiff: 'Outward',
	currConnects: 'Connects',
}

const getSeriesKey = item => DISPLAY_TITLES[item.kind] + ' ' + item.mountpoint;
const getUserTrafficSeriesKey = item => item.client + ' ' + item.mountpoint;
const getTrafficValue = item => item.final_value / 1024;
const getIntValue = item => item.final_value;

export default {
	components: {
		Row, Col, Card, Select, Affix,
		VerticalLayout, DateRangeChooser, TimeSeriesChart, StatsChartTitleWithDescription,
	},
	setup() {
		const { apiData, isLoading, doRequest } = Api.useCasterDynamicsGet();
		const sessionStore = useSessionStore();
		const { datesRange, datesRangeStr, datesRangeSafe } = useDateRangeAdapter(sessionStore, 1, 0);
		const usersSelected = ref([]);
		const { injectValue } = useProvideInjectWrapper;
		const wrapper = injectValue();

		const casterDynamicsGet = () => {
			if (datesRangeStr.value != null) {
				doRequest({
					start_date: datesRangeStr.value[0],
					end_date: datesRangeStr.value[1],
				});
			}
		};

		const allUsersOptions = computed(() => apiData.value.usernames.map(username => ({
			value: username,
			label: username,
		})));

		const trafficChartSeries = computed(() => generateSeries({
			items: apiData.value.items.filter(item => item.kind !== 'currConnects'),
			getSeriesKeyFn: getSeriesKey,
			getValueFn: getTrafficValue,
		}).series);
		const trafficChartOptions = computed(() => {
			let options = getBaseOptions('traffic', trafficChartSeries.value);
			options.yaxis.title.text = 'Kbytes / min';
			setXAxisRange(options, datesRangeSafe.value);
			options.tooltip.y.formatter = nullSafeFormatter(value => Math.round(value) + ' KB/min');
			return options;
		});

		const connectionsChartSeries = computed(() => generateSeries({
			items: apiData.value.items.filter(item => item.kind === 'currConnects'),
			getSeriesKeyFn: getSeriesKey,
			getValueFn: getIntValue,
		}).series);
		const connectionsChartOptions = computed(() => {
			let options = getBaseOptions('connections', connectionsChartSeries.value);
			options.yaxis.title.text = 'Active connections count';
			setXAxisRange(options, datesRangeSafe.value);
			options.tooltip.y.formatter = nullSafeFormatter(value => Math.round(value));
			return options;
		});

		const userTrafficChartSeries = computed(() => {
			const filteredUserTrafficItems = usersSelected.value.length > 0
				? apiData.value.userTrafficItems.filter(item => usersSelected.value.includes(item.client))
				: apiData.value.userTrafficItems;
			return generateSeries({
				items: filteredUserTrafficItems,
				getSeriesKeyFn: getUserTrafficSeriesKey,
				getValueFn: getTrafficValue,
			}).series;
		});
		const userTrafficChartOptions = computed(() => {
			let options = getBaseOptions('userTraffic', userTrafficChartSeries.value);
			options.yaxis.title.text = 'Kbytes / min';
			setXAxisRange(options, datesRangeSafe.value);
			options.tooltip.y.formatter = nullSafeFormatter(value => Math.round(value) + ' KB/min');
			return options;
		});

		onMounted(() => {
			casterDynamicsGet();
		});
		return {
			isLoading,
			datesRange,
			usersSelected,
			wrapper,
			allUsersOptions,

			trafficChartSeries,
			trafficChartOptions,
			connectionsChartSeries,
			connectionsChartOptions,
			userTrafficChartSeries,
			userTrafficChartOptions,

			casterDynamicsGet,
		};
	},
}
</script>
